import { GRID_CONFIG } from "../config";
import axios from "axios";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    FaSearch,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaCalendarAlt,
    FaTimes,
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const AuditViewer = () => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedAudit, setSelectedAudit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    

    // Function to fetch paginated data
    const fetchAuditLogs = (page, limit, search, start, end) => {
        setLoading(true);
        const params = { page, limit };
        if (search) params.search = search;
        if (start) {
            const s = new Date(start);
            s.setHours(0, 0, 0, 0);
            params.startDate = s.toISOString();
        }
        if (end) {
            const e = new Date(end);
            e.setHours(23, 59, 59, 999);
            params.endDate = e.toISOString();
        }
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/audit/paginated`, {
                params,
                withCredentials: true,
            })
            .then((res) => {
                setAuditLogs(res.data.data);
                setTotalRecords(res.data.pagination.totalItems);
                setTotalPages(res.data.pagination.totalPages);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                notifyUser(null, "error", "Failed to load audit logs", "toast", null, null, null, null, null, false, false, "top-center");
                setLoading(false);
            });
    };

    // Debounce search term — waits 400ms after typing stops before fetching
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Re-fetch whenever page, rows-per-page, debounced search, or date filters change
    useEffect(() => {
        fetchAuditLogs(currentPage, perPage, debouncedSearch, startDate, endDate);
    }, [currentPage, perPage, debouncedSearch, startDate, endDate]); // eslint-disable-line

    // Pagination display values (all server-side now)
    const displayRecords = totalRecords;
    const fromRecord = totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(currentPage * perPage, totalRecords);

    // Function to clear all filters
    const clearAllFilters = () => {
        setSearchTerm("");
        setDebouncedSearch("");
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
    };

    // Standardized export handler — fetches all filtered records and builds CSV
    const handleExportChangeLogsCSV = async () => {
        try {
            const params = {};
            if (startDate) {
                const s = new Date(startDate);
                s.setHours(0, 0, 0, 0);
                params.startDate = s.toISOString();
            }
            if (endDate) {
                const e = new Date(endDate);
                e.setHours(23, 59, 59, 999);
                params.endDate = e.toISOString();
            }
            if (searchTerm) params.search = searchTerm;

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/audit/export`,
                { params, withCredentials: true },
            );

            const allData = response.data || [];
            if (!allData.length) {
                notifyUser(null, "info", "No records to export", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            const rows = allData.map((log) => ({
                ID: log._ID || "",
                Source: log.CHANGED_SRC || "",
                "Source ID": log.CHANGED_SRC_ID ? String(log.CHANGED_SRC_ID) : "",
                "Change Type": log.CHANGE_TYPE === "I" ? "Insert" : log.CHANGE_TYPE === "U" ? "Update" : log.CHANGE_TYPE === "D" ? "Delete" : log.CHANGE_TYPE || "",
                User: log.CHANGED_BY || "",
                "Date/Time": new Date(log.CHANGED_ON).toLocaleString(),
                "Site ID": log._SITE_ID ? String(log._SITE_ID) : "",
                "Before Change": typeof log.BEFORE_CHANGE === "string" ? log.BEFORE_CHANGE : JSON.stringify(log.BEFORE_CHANGE || {}),
                "After Change": typeof log.AFTER_CHANGE === "string" ? log.AFTER_CHANGE : JSON.stringify(log.AFTER_CHANGE || {}),
            }));

            const header = Object.keys(rows[0]).join(",");
            const csvBody = rows
                .map((r) =>
                    Object.values(r)
                        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
                        .join(","),
                )
                .join("\n");

            const csv = `${header}\n${csvBody}`;
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            const now = new Date();
            const filename = `audit_logs_${now.toISOString().split("T")[0]}.csv`;
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error exporting audit logs:", error);
            notifyUser(null, "error", "Failed to export audit logs", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Custom Pagination Component
    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing {displayRecords === 0 ? 0 : fromRecord} to {toRecord} of{" "}
                {displayRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    const columns = [
        {
            name: "ID",
            selector: (row) => row._ID,
            sortable: true,
            grow: 0.8,
        },
        {
            name: "Source",
            selector: (row) => row.CHANGED_SRC,
            sortable: true,
            grow: 1.5,
        },
        {
            name: "Source ID",
            selector: (row) => row.CHANGED_SRC_ID,
            grow: 1.2,
        },
        {
            name: "Type",
            selector: (row) => {
                const base =
                    "inline-block w-20 text-center text-xs font-semibold px-2 py-1 rounded-full shadow-sm";
                const map = {
                    I: (
                        <span
                            className={`${base} bg-green-100 text-green-700 border border-green-200`}
                        >
                            Insert
                        </span>
                    ),
                    U: (
                        <span
                            className={`${base} bg-yellow-100 text-yellow-700 border border-yellow-200`}
                        >
                            Update
                        </span>
                    ),
                    D: (
                        <span
                            className={`${base} bg-red-100 text-red-700 border border-red-200`}
                        >
                            Delete
                        </span>
                    ),
                };
                return map[row.CHANGE_TYPE] || row.CHANGE_TYPE;
            },
            sortable: true,
            grow: 0.8,
        },
        {
            name: "User",
            selector: (row) => row.CHANGED_BY,
            grow: 1.2,
        },
        {
            name: "Date/Time",
            selector: (row) => new Date(row.CHANGED_ON).toLocaleString(),
            sortable: true,
            sortFunction: (rowA, rowB) => {
                const dateA = new Date(rowA.CHANGED_ON);
                const dateB = new Date(rowB.CHANGED_ON);
                return dateB.getTime() - dateA.getTime();
            },
            grow: 1.5,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "50px",
                "&:hover": {
                    backgroundColor: "#f1f8ff",
                },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    const renderJSON = (json) => {
        try {
            const obj = typeof json === "string" ? JSON.parse(json) : json;
            return (
                <div className="space-y-1 text-sm text-gray-800">
                    {Object.entries(obj).map(([key, value]) => (
                        <div
                            key={key}
                            style={{
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                            }}
                        >
                            <strong className="capitalize">{key}:</strong>{" "}
                            <span
                                style={{
                                    display: "inline-block",
                                    maxWidth: "100%",
                                }}
                            >
                                {String(value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        } catch{
            return <span className="text-red-500">empty </span>;
        }
    };

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                CHANGE LOGS
            </h2>

            <div className="aqi-grid-toolbar">
                {/* Rows per page */}
                <select
                    className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                    style={{
                        padding: "0.375rem 2rem 0.375rem 0.75rem",
                        color: "var(--navy)",
                        backgroundColor: "#F5F7FA",
                        border: "2px solid #C2D7E2",
                        borderRadius: "0.375rem",
                        fontWeight: "500",
                        appearance: "none",
                        backgroundImage:
                            "var(--navy-select-chevron)",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 0.5rem center",
                        backgroundSize: "16px 12px",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        outline: "none",
                    }}
                    value={perPage}
                    onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                    disabled={loading}
                >
                    {GRID_CONFIG.pageSizeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>

                {/* Date range picker container */}
                <div className="flex gap-2 items-center flex-wrap">
                    <div className="relative">
                        <div className="aqi-grid-control flex items-center border-2 border-[#C2D7E2] rounded-md bg-[#F5F7FA] px-2 cursor-pointer text-navy hover:border-navy focus-within:border-navy focus-within:ring-1 focus-within:ring-navy focus-within:shadow-sm transition-all">
                            <FaCalendarAlt className="text-navy mr-2" />
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => {
                                    setStartDate(date);
                                    setCurrentPage(1);
                                }}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                placeholderText="Start Date"
                                dateFormat="MM/dd/yyyy"
                                className="bg-transparent border-0 outline-none text-sm p-1 w-28"
                            />
                            {startDate && (
                                <button
                                    onClick={() => setStartDate(null)}
                                    className="text-gray-400 hover:text-red-500"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "0 4px",
                                    }}
                                >
                                    <FaTimes size={12} />
                                </button>
                            )}
                        </div>
                    </div>

                    <span className="text-navy">to</span>

                    <div className="relative">
                        <div className="aqi-grid-control flex items-center border-2 border-[#C2D7E2] rounded-md bg-[#F5F7FA] px-2 cursor-pointer text-navy hover:border-navy focus-within:border-navy focus-within:ring-1 focus-within:ring-navy focus-within:shadow-sm transition-all">
                            <FaCalendarAlt className="text-navy mr-2" />
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => {
                                    setEndDate(date);
                                    setCurrentPage(1);
                                }}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                placeholderText="End Date"
                                dateFormat="MM/dd/yyyy"
                                className="bg-transparent border-0 outline-none text-sm p-1 w-28"
                            />
                            {endDate && (
                                <button
                                    onClick={() => setEndDate(null)}
                                    className="text-gray-400 hover:text-red-500"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "0 4px",
                                    }}
                                >
                                    <FaTimes size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Search bar */}
                <div className="aqi-grid-search">
                    <FaSearch
                        className="absolute left-3 top-2.5"
                        style={{ color: "var(--navy)" }}
                    />
                    <input
                        type="text"
                        placeholder="Search audit logs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            paddingTop: "0.5rem",
                            paddingBottom: "0.5rem",
                            width: "100%",
                            borderRadius: "0.375rem",
                            border: "2px solid #C2D7E2",
                            backgroundColor: "#F5F7FA",
                            transition: "all 0.2s ease",
                            outline: "none",
                            color: "var(--navy)",
                        }}
                        className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                    />
                </div>

                {/* Clear filters button */}
                {(searchTerm || startDate || endDate) && (
                    <button
                        onClick={clearAllFilters}
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        <FaTimes size={14} />
                        <span>Clear Filters</span>
                    </button>
                )}

                {/* Export to CSV button */}
                <div className="aqi-grid-toolbar-actions">
                    <button
                        onClick={handleExportChangeLogsCSV}
                        disabled={totalRecords === 0}
                        className={`aqi-grid-button aqi-grid-button--primary ${
                            totalRecords === 0 ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                    >
                        Export
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={auditLogs}
                customStyles={customStyles}
                progressPending={loading}
                pagination={false}
                onRowClicked={(row) => setSelectedAudit(row)}
                highlightOnHover
                pointerOnHover
                responsive
                striped
                defaultSortField="_ID"
                defaultSortAsc={false}
            />

            {/* Custom Pagination UI */}
            <CustomPagination />

            {/* Audit Details Modal */}
            {selectedAudit && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "30px",
                            borderRadius: "8px",
                            width: "90%",
                            maxWidth: "1000px",
                            maxHeight: "85vh",
                            overflow: "auto",
                            boxShadow: "0 0 30px rgba(0,0,0,0.2)",
                            overflowX: "hidden",
                            wordWrap: "break-word",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px",
                                borderBottom: "1px solid #eee",
                                paddingBottom: "10px",
                            }}
                        >
                            <h2 style={{ color: "#007bff", margin: 0 }}>
                                Audit Details (ID #{selectedAudit._ID})
                            </h2>
                            <button
                                onClick={() => setSelectedAudit(null)}
                                style={{
                                    background: "#007bff",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "8px 15px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                }}
                            >
                                Close
                            </button>
                        </div>

                        {/* Header information in a grid */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fill, minmax(200px, 1fr))",
                                gap: "15px",
                                marginBottom: "20px",
                                width: "100%",
                            }}
                        >
                            <div
                                style={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                }}
                            >
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Changed Source:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedAudit.CHANGED_SRC}
                                </p>
                            </div>
                            <div
                                style={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                }}
                            >
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Source ID:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedAudit.CHANGED_SRC_ID}
                                </p>
                            </div>
                            <div
                                style={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                }}
                            >
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Change Type:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedAudit.CHANGE_TYPE === "I"
                                        ? "Insert"
                                        : selectedAudit.CHANGE_TYPE === "U"
                                          ? "Update"
                                          : selectedAudit.CHANGE_TYPE === "D"
                                            ? "Delete"
                                            : selectedAudit.CHANGE_TYPE}
                                </p>
                            </div>
                            <div
                                style={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                }}
                            >
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Changed By:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedAudit.CHANGED_BY}
                                </p>
                            </div>
                            <div
                                style={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                }}
                            >
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Changed On:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {new Date(
                                        selectedAudit.CHANGED_ON,
                                    ).toLocaleString()}
                                </p>
                            </div>
                            {selectedAudit._SITE_ID && (
                                <div
                                    style={{
                                        wordBreak: "break-word",
                                        overflowWrap: "break-word",
                                    }}
                                >
                                    <h3
                                        style={{
                                            color: "#555",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        Site ID:
                                    </h3>
                                    <p style={{ margin: 0 }}>
                                        {selectedAudit._SITE_ID}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Before/After Change side by side */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: "20px",
                                marginBottom: "20px",
                                width: "100%",
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "#F5F7FA",
                                    padding: "15px",
                                    borderRadius: "4px",
                                    borderLeft: "4px solid #dc3545",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                    minWidth: 0,
                                }}
                            >
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "10px",
                                    }}
                                >
                                    Before Change
                                </h3>
                                {renderJSON(selectedAudit.BEFORE_CHANGE)}
                            </div>
                            <div
                                style={{
                                    backgroundColor: "#F5F7FA",
                                    padding: "15px",
                                    borderRadius: "4px",
                                    borderLeft: "4px solid #28a745",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                    minWidth: 0,
                                }}
                            >
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "10px",
                                    }}
                                >
                                    After Change
                                </h3>
                                {renderJSON(selectedAudit.AFTER_CHANGE)}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditViewer;
